import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";

const TOTAL_FRAMES = 1097;
const ASSETS_PATH = "/gym-tour-frames";

export default function GymTour() {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [isRenderable, setIsRenderable] = useState(false);

  const framesRef = useRef([]);

  useEffect(() => {
    // Preload logic
    let tempImagesLoaded = 0;
    
    // We preload in chunks so we don't block the browser completely
    // but the array handles it fine.
    for (let i = 1; i <= TOTAL_FRAMES; i++) {
      const img = new Image();
      const num = String(i).padStart(4, "0");
      img.src = `${process.env.PUBLIC_URL || ""}${ASSETS_PATH}/frame_${num}.png`;
      img.onload = () => {
        tempImagesLoaded++;
        setImagesLoaded(tempImagesLoaded);
        if (tempImagesLoaded >= Math.min(TOTAL_FRAMES, 50)) {
            setIsRenderable(true);
        }
      };
      // Keep reference for drawing
      framesRef.current[i - 1] = img;
    }
  }, []);

  const drawFrame = (frameIndex) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = framesRef.current[frameIndex];
    
    if (img && img.complete) {
      const cw = window.innerWidth;
      const ch = window.innerHeight;
      canvas.width = cw;
      canvas.height = ch;
      
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const scale = Math.max(cw / iw, ch / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = (cw - dw) / 2;
      const dy = (ch - dh) / 2;
      
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, dx, dy, dw, dh);
    }
  };

  useEffect(() => {
    // Initial draw once renderable
    if (isRenderable) {
      drawFrame(0);
    }
  }, [isRenderable]);

  useEffect(() => {
    let rafId = null;

    const handleScroll = () => {
      if (!containerRef.current) return;
      
      // Throttle with rAF
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const scrollPos = window.scrollY;
        
        // Use a slight buffer so we never exceed bounds
        const maxScroll = containerRef.current.scrollHeight - window.innerHeight;
        const scrollFraction = Math.max(0, Math.min(1, scrollPos / maxScroll));
        
        setScrollProgress(scrollFraction);

        const frameIndex = Math.min(
          TOTAL_FRAMES - 1,
          Math.floor(scrollFraction * TOTAL_FRAMES)
        );
        
        drawFrame(frameIndex);
        rafId = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!containerRef.current) return;
        const scrollPos = window.scrollY;
        const maxScroll = containerRef.current.scrollHeight - window.innerHeight;
        const scrollFraction = Math.max(0, Math.min(1, scrollPos / maxScroll));
        const frameIndex = Math.min(TOTAL_FRAMES - 1, Math.floor(scrollFraction * TOTAL_FRAMES));
        drawFrame(frameIndex);
      }, 100);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div ref={containerRef} className="relative bg-[#0A0A0A] w-full" style={{ height: "1500vh" }}>
      {/* Loading Overlay */}
      {!isRenderable && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center p-6 text-white text-center">
            <span className="font-['Bebas_Neue'] tracking-widest text-4xl mb-4 text-[#F59E0B]">CORE CULTURE</span>
            <div className="w-full max-w-xs h-1 bg-white/20 rounded-full mb-2 overflow-hidden">
                <div className="h-full bg-[#f97316] transition-all duration-300 rounded-full" style={{ width: `${(imagesLoaded / Math.min(TOTAL_FRAMES, 50)) * 100}%` }} />
            </div>
            <p className="font-mono text-xs uppercase tracking-widest text-gray-500">Loading Tour Details...</p>
        </div>
      )}

      {/* Canvas */}
      <div className="fixed top-0 left-0 w-full h-full z-0 bg-black overflow-hidden pointer-events-none">
        <canvas ref={canvasRef} className="w-full h-full object-cover" />
      </div>

      {/* Overlay UI */}
      <div className="fixed top-0 left-0 w-full z-50 p-6 flex flex-col sm:flex-row justify-between items-start gap-4 pointer-events-none">
        <Link to="/" className="flex items-center gap-2 text-white bg-black/60 backdrop-blur-md px-6 py-3 rounded-full pointer-events-auto hover:bg-[#F59E0B] hover:text-black transition-all shadow-xl group border border-white/5">
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-bold tracking-widest text-xs uppercase">RETURN</span>
        </Link>
        
        <div className="flex flex-col items-end gap-2 bg-black/40 backdrop-blur-sm p-4 rounded-2xl border border-white/5">
          <span className="text-white/80 text-[10px] font-mono uppercase tracking-[0.2em]">{Math.round(scrollProgress * 100)}% EXPLORED</span>
          <div className="w-24 sm:w-32 h-1 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#F59E0B] to-[#f97316] rounded-full transition-none" style={{ width: `${scrollProgress * 100}%` }} />
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      {scrollProgress < 0.03 && isRenderable && (
        <div className="fixed bottom-12 left-1/2 -translate-x-1/2 z-40 animate-bounce pointer-events-none drop-shadow-[0_0_15px_rgba(245,158,11,0.5)]">
          <div className="bg-black/50 backdrop-blur-md p-3 rounded-full border border-white/10 flex items-center justify-center">
            <ChevronDown size={28} className="text-[#F59E0B]" />
          </div>
        </div>
      )}
    </div>
  );
}
